import React from 'react';
import { View, ActivityIndicator } from 'react-native';
import { Text } from '@shared/components/text';
import { Head } from '@shared/components/head';
import Button from '@shared/components/md-design-system/Button/Button';
import { Icon } from '@ant-design/react-native';
import { colorPalette } from '@shared/config/colorsConfig';
import { dynamicTranslations } from '@shared/utils/dynamicTranslation';
import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { TRootStackParamList } from 'app/types/navigation';
import { useReportDownload } from '../../hooks';
import { TelemonitoringReportCardProps } from '../../types';
import { Badge } from '@shared/components/md-design-system/Badge';

type NavigationProp = NativeStackNavigationProp<TRootStackParamList>;

export const TelemonitoringReportCard: React.FC<TelemonitoringReportCardProps> = ({
    reportData,
    openedByPatient = true,
    pdfUrl,
}) => {
    const { t } = dynamicTranslations();
    const navigation = useNavigation<NavigationProp>();
    const { downloadAndViewReport, isDownloading, downloadError } = useReportDownload();

    const formatDate = (dateString: string) => {
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString('it-IT', {
                day: '2-digit',
                month: '2-digit',
                year: '2-digit'
            });
        } catch (error) {
            return dateString;
        }
    };

    const isGenerating = reportData.status === 'generating';
    const isCurrentlyDownloading = isDownloading === reportData.id;

    const handleViewReport = async () => {
        if (pdfUrl) {
            navigation.navigate('PdfDocumentPage', { pdfUrl });
        } else {
            await downloadAndViewReport(reportData.id, reportData.title);
        }
    };

    return (
        <View className="bg-white rounded-2xl mb-4 border border-gray-200 p-4">
            <View className="flex-row items-start justify-between mb-1">
                <Head type="h3" className="text-black flex-1 mr-3">
                    {!openedByPatient ? t('telemonitoring.report.newReportTitle') : reportData.title || t('telemonitoring.report.defaultTitle')}
                </Head>
                {!openedByPatient &&
                    <Badge variant='warning' size='large'>
                        {t('telemonitoring.report.newReportBadgeText')}
                    </Badge>
                }
            </View>

            {openedByPatient && (
                <Text className="text-base font-bold mb-1">
                    ({t('telemonitoring.report.dateRange', {
                        startDate: formatDate(reportData.startDate),
                        endDate: formatDate(reportData.endDate)
                    })})
                </Text>
            )}

            <Text className="text-base text-gray-500 mb-2">
                {t('telemonitoring.report.receivedOn')}{' '}
                {formatDate(reportData.receivedDate)}
            </Text>

            {downloadError && (
                <View className="flex-row items-center mb-3 p-3 bg-red-50 rounded-lg border border-red-200">
                    <Icon name="exclamation-circle" size={16} color={colorPalette.error[500]} />
                    <Text className="text-sm text-red-800 ml-2">{downloadError}</Text>
                </View>
            )}

            <View className="flex-row gap-3 mt-1">
                {isGenerating ? (
                    <View className="flex-row items-center gap-2">
                        <ActivityIndicator size="small" color={colorPalette.gray[400]} />
                        <Text className="text-sm text-gray-500">{t('telemonitoring.report.generating')}</Text>
                    </View>
                ) : (
                    <View className="self-start">
                        <Button
                            icon="eye"
                            variant="primary"
                            onPress={handleViewReport}
                            disabled={isCurrentlyDownloading}
                        >
                            {isCurrentlyDownloading
                                ? t('telemonitoring.report.downloading')
                                : t('telemonitoring.report.view')
                            }
                        </Button>
                    </View>
                )}
            </View>
        </View>
    );
};
